import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { usarNumero, type OpcionLector } from './usarNumero';

/** La etiqueta de un control, con el mismo aspecto en toda la aplicación. */
function Etiqueta({ children }: { children: ReactNode }) {
  return <span className="etiqueta">{children}</span>;
}

function Ayuda({ children }: { children: ReactNode }) {
  return <span className="ayuda">{children}</span>;
}

/** El borde, el fondo y el foco de todo control salen de `@tanteo/compartido/controles.css`. */
const CLASES_CONTROL = 'control';

export function Campo({
  etiqueta,
  ayuda,
  className = '',
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { etiqueta?: string; ayuda?: string }) {
  return (
    <label className="block">
      {etiqueta && <Etiqueta>{etiqueta}</Etiqueta>}
      <input className={`${CLASES_CONTROL} ${className}`} {...props} />
      {ayuda && <Ayuda>{ayuda}</Ayuda>}
    </label>
  );
}

/** Celular colombiano con el prefijo +57 a la vista, para que nadie lo escriba dentro del campo. */
export function CampoCelular({
  etiqueta = 'Celular',
  className = '',
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { etiqueta?: string }) {
  return (
    <label className="block">
      <Etiqueta>{etiqueta}</Etiqueta>
      <span className="celular">
        <span className="celular-prefijo">+57</span>
        <input
          inputMode="numeric"
          autoComplete="tel"
          placeholder="300 000 0000"
          className={`${CLASES_CONTROL} ${className}`}
          {...props}
        />
      </span>
    </label>
  );
}

/**
 * Campo numérico con etiqueta: alineado a la derecha, con la tipografía de números y el
 * comportamiento de `usarNumero` — se puede vaciar, selecciona al enfocar y no trae flechas.
 */
export function CampoNumero({
  className = '',
  valor,
  onValor,
  min,
  max,
  decimales,
  plata,
  lector,
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'min' | 'max' | 'type'> & {
  etiqueta?: string;
  ayuda?: string;
  valor: number | string | null | undefined;
  onValor: (n: number | null) => void;
  min?: number;
  max?: number;
  decimales?: boolean;
  /** Separador de miles en lo que se ve: solo para dinero. */
  plata?: boolean;
  lector?: OpcionLector;
}) {
  return (
    <Campo
      {...usarNumero({ valor, onValor, min, max, decimales, plata, lector })}
      className={`num text-right ${className}`}
      {...props}
    />
  );
}

/** Área de texto con el mismo borde y foco que los campos. */
export function AreaTexto({
  etiqueta,
  ayuda,
  className = '',
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { etiqueta?: string; ayuda?: string }) {
  return (
    <label className="block">
      {etiqueta && <Etiqueta>{etiqueta}</Etiqueta>}
      <textarea className={`${CLASES_CONTROL} ${className}`} {...props} />
      {ayuda && <Ayuda>{ayuda}</Ayuda>}
    </label>
  );
}

export function CampoLista({
  etiqueta,
  children,
  className = '',
  ...props
}: SelectHTMLAttributes<HTMLSelectElement> & { etiqueta?: string }) {
  return (
    <label className="block">
      {etiqueta && <Etiqueta>{etiqueta}</Etiqueta>}
      <select className={`${CLASES_CONTROL} ${className}`} {...props}>
        {children}
      </select>
    </label>
  );
}

/**
 * Fila de campos de igual ancho, que se apilan en pantalla angosta. Todos los formularios de
 * persona usan esta rejilla: con `flex-1` el ancho depende del contenido y la fila queda desigual.
 */
export function FilaCampos({ children, columnas = 2 }: { children: ReactNode; columnas?: 2 | 3 }) {
  return (
    <div className={`fila-campos ${columnas === 3 ? 'fila-campos--tres' : ''}`}>{children}</div>
  );
}
